import React, { useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/sidebar.css';

const Sidebar = ({ isSuperAdmin, isOpen, setIsOpen }) => {
  const { hasPermission, hasModule, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close drawer after navigation on mobile
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, setIsOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('sidebar-drawer-open');
    } else {
      document.body.classList.remove('sidebar-drawer-open');
    }
    return () => document.body.classList.remove('sidebar-drawer-open');
  }, [isOpen]);

  const menuItems = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      icon: '📊',
      permission: 'view_reports'
    },
    {
      title: 'POS',
      path: '/pos',
      icon: '🛒',
      module: 'pos'
    },
    {
      title: 'Inventory',
      path: '/products',
      icon: '📦',
      permission: 'manage_products',
      module: 'inventory',
      submenu: [
        { title: 'All Products', path: '/products' },
        { title: 'Categories', path: '/products/categories' },
        { title: 'Suppliers', path: '/products/suppliers' },
        { title: 'Low Stock', path: '/products/low-stock' },
        { title: 'Stock Movements', path: '/products/movements' },
        { title: 'Bulk Upload', path: '/products/bulk-upload' }
      ]
    },
    {
      title: 'CRM',
      path: '/customers',
      icon: '👥',
      permission: 'view_customers',
      module: 'crm'
    },
    {
      title: 'Finance',
      path: '/finance',
      icon: '💰',
      permission: 'manage_debts',
      module: 'finance'
    },
    {
      title: 'AI Insights',
      path: '/ai',
      icon: '🤖',
      permission: 'view_reports',
      module: 'ai_bi'
    },
    {
      title: 'Staff',
      path: '/staff',
      icon: '👥',
      permission: 'manage_cashiers'
    },
    {
      title: 'Reports',
      path: '/reports',
      icon: '📈',
      permission: 'view_reports'
    },
    {
      title: 'Settings',
      path: '/settings',
      icon: '⚙️',
      permission: 'manage_settings'
    }
  ];

  const adminMenuItems = [
    { title: 'Admin Dashboard', path: '/admin', icon: '👑' },
    { title: 'Homepage Content', path: '/admin/homepage', icon: '🏠' },
    { title: 'App Modules', path: '/admin/modules', icon: '🧩' },
    { title: 'M-Pesa Billing', path: '/admin/mpesa', icon: '📲' },
    { title: 'Subscription Payments', path: '/admin/subscription-payments', icon: '💳' },
    { title: 'Businesses', path: '/admin/businesses', icon: '🏢' },
    { title: 'Subscription Plans', path: '/admin/plans', icon: '📋' },
    { title: 'System Users', path: '/admin/users', icon: '👤' },
    { title: 'Audit Logs', path: '/admin/audit', icon: '📜' },
    { title: 'Platform Settings', path: '/settings', icon: '⚙️' }
  ];

  const closeDrawer = () => setIsOpen(false);

  const handleLogout = () => {
    closeDrawer();
    logout();
    navigate('/login', { replace: true });
  };

  const renderMenuItems = (items) =>
    items.map((item) => {
      if (item.permission && !hasPermission(item.permission)) {
        return null;
      }
      if (item.module && !hasModule(item.module)) {
        return null;
      }

      if (item.submenu) {
        return (
          <div key={item.path} className="nav-item has-submenu">
            <div className="nav-link-parent">
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-title">{item.title}</span>
            </div>
            <div className="submenu">
              {item.submenu.map((sub) => (
                <NavLink
                  key={sub.path}
                  to={sub.path}
                  end={sub.path === '/products'}
                  className={({ isActive }) =>
                    `subnav-link ${isActive ? 'active' : ''}`
                  }
                  onClick={closeDrawer}
                >
                  {sub.title}
                </NavLink>
              ))}
            </div>
          </div>
        );
      }

      return (
        <NavLink
          key={item.path}
          to={item.path}
          end={item.path === '/dashboard' || item.path === '/admin'}
          className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
          onClick={closeDrawer}
        >
          <span className="nav-icon">{item.icon}</span>
          <span className="nav-title">{item.title}</span>
        </NavLink>
      );
    });

  return (
    <>
      {isOpen && (
        <button
          type="button"
          className="sidebar-overlay"
          aria-label="Close menu"
          onClick={closeDrawer}
        />
      )}
      <aside
        className={`sidebar ${isOpen ? 'open' : ''}`}
        aria-hidden={!isOpen ? undefined : false}
      >
        <div className="sidebar-header">
          <div className="logo">
            <span className="logo-icon">🛍️</span>
            <span className="logo-text">Harvyq</span>
          </div>
          <button
            type="button"
            className="close-sidebar-btn"
            aria-label="Close sidebar"
            onClick={closeDrawer}
          >
            ✕
          </button>
        </div>

        <nav className="sidebar-nav">
          {!isSuperAdmin && (
            <div className="nav-section">
              <div className="nav-section-title">Main</div>
              {renderMenuItems(menuItems)}
            </div>
          )}

          {isSuperAdmin && (
            <div className="nav-section">
              <div className="nav-section-title">Platform</div>
              {renderMenuItems(adminMenuItems)}
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-name">
              {user?.first_name} {user?.last_name}
            </div>
            <div className="user-role">{user?.role}</div>
          </div>
          <button type="button" className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
